import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import { ArrowLeft, CheckCircle2, History, Info, Timer, User, XCircle } from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { Field } from "@/components/forms";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/utils/formatters";
import { cn } from "@/lib/utils";

import { usePartnerWorkLog, useReviewWorkLog } from "@/hooks/useWorkLogs";
import { PageSkeleton } from "@/components/shared/Skeletons";

export const Route = createFileRoute("/partner/work-logs/$id")({ component: PartnerWorkLogDetail });

function PartnerWorkLogDetail() {
  const { id } = Route.useParams();
  const { data: log, isLoading } = usePartnerWorkLog(id);
  const reviewMutation = useReviewWorkLog();

  const [decision, setDecision] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!log) {
    return (
      <EmptyState
        icon={<XCircle />}
        title="Work log not found."
        description="This submission may have been removed or is no longer assigned to you."
      />
    );
  }

  const handleReview = async () => {
    try {
      await reviewMutation.mutateAsync({ id, status: decision, feedback });
      setSubmitted(true);
    } catch (err) {
      console.error("Failed to submit review", err);
    }
  };

  const studentUniversity = log.student_university || log.studentUniversity || "";
  const studentMajor = log.student_major || log.studentMajor || "";
  const studentYear = log.student_year ?? log.studentYear ?? 0;
  const studentApproved = log.student_approved_logs ?? log.studentApprovedLogs ?? 0;
  const studentReviewed = log.student_reviewed_logs ?? log.studentReviewedLogs ?? 0;
  const approvalPct =
    studentReviewed > 0 ? Math.round((studentApproved / studentReviewed) * 100) : null;
  const courseLine = [studentMajor, studentYear ? `Year ${studentYear}` : ""]
    .filter(Boolean)
    .join(", ");
  const hasProfile = Boolean(studentUniversity || studentMajor || studentYear);

  if (submitted) {
    const approved = decision === "approved";
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center py-16 text-center">
        <div
          className={cn(
            "mb-5 flex size-12 items-center justify-center rounded-md border",
            approved
              ? "border-success/30 bg-success/10 text-success"
              : "border-destructive/30 bg-destructive/10 text-destructive",
          )}
        >
          {approved ? (
            <CheckCircle2 className="size-6" aria-hidden />
          ) : (
            <XCircle className="size-6" aria-hidden />
          )}
        </div>
        <h1 className="text-display-sm text-foreground">Review completed</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          The student has been notified that their work log was {decision}. Your feedback has been
          saved.
        </p>
        <Button asChild className="mt-6">
          <Link to="/partner/dashboard">Return to dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Review submission"
        subtitle="Carefully review the work completed, recorded hours, and supporting notes before approving the log."
        breadcrumbs={[
          { label: "Dashboard", path: "/partner/dashboard" },
          { label: "Work Log Reviews", path: "/partner/work-logs" },
          { label: "Review Submission" },
        ]}
        action={
          <Button variant="outline" asChild>
            <Link to="/partner/work-logs">
              <ArrowLeft />
              Back to reviews
            </Link>
          </Button>
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <SectionCard title="Submission details">
          <div className="mb-5 flex items-center gap-3">
            <Avatar className="size-11 rounded-full">
              <AvatarFallback className="rounded-full text-base">
                {log.student_name?.charAt(0) || "S"}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h2 className="text-title text-foreground">{log.student_name || "Student"}</h2>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-caption text-muted-foreground">
                  Submitted on {formatDate(log.date || log.created_at)}
                </span>
                <StatusChip status={log.status} />
              </div>
            </div>
          </div>

          <div className="mb-5 grid gap-3 md:grid-cols-2">
            <MetaCard icon={<Timer />} label="Total hours" value={`${log.hours} hours recorded`} />
          </div>

          <h3 className="eyebrow mb-1.5">Tasks and achievements</h3>
          <p className="mb-5 text-sm leading-7 whitespace-pre-line text-body">
            {log.description || log.content || "No description was provided."}
          </p>

          {(log.challenges || log.challenge) && (
            <>
              <h3 className="eyebrow mb-1.5">Challenges reported</h3>
              <p className="mb-5 text-sm leading-7 whitespace-pre-line text-body">
                {log.challenges || log.challenge}
              </p>
            </>
          )}

          <div className="space-y-4 border-t border-border pt-5">
            <h3 id="decision-label" className="text-base font-medium text-foreground">
              Final decision
            </h3>
            <RadioGroup
              value={decision}
              onValueChange={setDecision}
              aria-labelledby="decision-label"
              className="grid gap-2 md:grid-cols-2"
            >
              <DecisionOption
                value="approved"
                selected={decision === "approved"}
                tone="success"
                label="Approve submission"
              />
              <DecisionOption
                value="rejected"
                selected={decision === "rejected"}
                tone="destructive"
                label="Reject submission"
              />
            </RadioGroup>

            <Field label="Decision feedback">
              <Textarea
                rows={4}
                placeholder="Provide context for your decision. If rejecting, specify what should be corrected."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
            </Field>

            <Button
              className="w-full"
              disabled={!decision}
              loading={reviewMutation.isPending}
              onClick={handleReview}
            >
              Confirm review decision
            </Button>
          </div>
        </SectionCard>

        <div className="flex min-w-0 flex-col gap-4">
          <SectionCard title="Student context">
            <div className="space-y-3">
              {hasProfile && (
                <ContextRow
                  icon={<User />}
                  title={studentUniversity || "Student profile"}
                  subtitle={courseLine || "Course details not provided"}
                />
              )}
              {hasProfile && studentReviewed > 0 && <div className="border-t border-border" />}
              {studentReviewed > 0 && (
                <ContextRow
                  icon={<History />}
                  title="Previous stats"
                  subtitle={`${studentApproved}/${studentReviewed} logs approved (${approvalPct}%)`}
                />
              )}
              {!hasProfile && studentReviewed === 0 && (
                <p className="text-sm text-muted-foreground">
                  No profile details or review history for this student yet.
                </p>
              )}
            </div>
          </SectionCard>

          <section className="rounded-md border border-border bg-muted/40 p-4">
            <h2 className="mb-1 flex items-center gap-2 text-sm font-medium text-foreground">
              <Info className="size-4 text-muted-foreground" aria-hidden />
              Decision impact
            </h2>
            <p className="text-sm leading-6 text-muted-foreground">
              Approved hours count toward the student's credit requirements. Rejections give the
              student room to correct and resubmit.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function MetaCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-sm border border-border bg-background p-3">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-card text-body [&_svg]:size-4"
        aria-hidden
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="eyebrow">{label}</p>
        <p className="text-sm font-medium text-foreground tabular-nums">{value}</p>
      </div>
    </div>
  );
}

function DecisionOption({
  value,
  selected,
  tone,
  label,
}: {
  value: string;
  selected: boolean;
  tone: "success" | "destructive";
  label: string;
}) {
  const id = `decision-${value}`;
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-center gap-3 rounded-sm border p-3 transition-colors",
        selected
          ? tone === "success"
            ? "border-success/40 bg-success/10"
            : "border-destructive/40 bg-destructive/10"
          : "border-border hover:bg-muted/60",
      )}
    >
      <RadioGroupItem id={id} value={value} />
      <span className="text-sm font-medium text-foreground">{label}</span>
    </label>
  );
}

function ContextRow({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body [&_svg]:size-4"
        aria-hidden
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-caption text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}
